import { FC, ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAtomValue } from "jotai";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  ArrowBackOutlined,
  CheckCircleOutline,
  ExpandMore,
  ErrorOutline,
  LocalShippingOutlined,
  PaymentOutlined,
  PersonOutline,
  ShoppingCartOutlined,
  TuneOutlined,
} from "@mui/icons-material";
import {
  Create,
  SaveButton,
  SimpleForm,
  Toolbar,
  useNotify,
  FormGroupContextProvider,
} from "react-admin";
import { useFormState } from "react-hook-form";

import { country } from "@/config/country";
import { PURCHASES } from "@/config/resources";
import { SEERI_SUPPLIER_ID } from "@/constants/env";
import { useAdmin } from "@/hooks/admin";
import { PricingV2 } from "./PricingV2";

import { PrePurchaseListener, ShippingInputs } from "../post";
import { PaymentStep } from "./PaymentStep";
import { BillingAdressDropdown } from "../post/billing-address";
import { ClientOption } from "../post/client";
import { prePurchaseAtom, prePurchaseErrorAtom } from "../post/store";
import { ProductPickerV2 } from "./ProductPickerV2";
import { ClientAutocomplete } from "./ClientAutocomplete";
import { ClientModalV2 } from "./ClientModalV2";
import { AddressModalV2 } from "./AddressModalV2";
import { useRefresh } from "react-admin";
import { SELLER_ADDRESSES } from "@/config/resources";
import { Button as RaButton } from "react-admin";
import { CreateRounded } from "@mui/icons-material";
import { useFormContext, useWatch } from "react-hook-form";

/**
 * PurchaseCreateV2
 *
 * Friendlier single-page rewrite of the purchase creation flow. Reuses the
 * existing inputs (seller / client / products / payment / shipping) and the
 * PrePurchaseListener that drives live pricing, but lays them out as four
 * numbered "steps" with a sticky summary on the right. Hardcodes SELL_OUT.
 */
export const PurchaseCreateV2 = () => {
  const { hasPermission } = useAdmin();
  if (!hasPermission("purchase.create")) return null;

  return (
    <>
      <Create
        resource={PURCHASES}
        redirect="show"
        sx={{
          "& .RaCreate-main": { mt: 0 },
          "& .RaCreate-card": {
            boxShadow: "none",
            background: "transparent",
          },
        }}
      >
        <SimpleForm
          mode="onChange"
          warnWhenUnsavedChanges
          toolbar={false}
          defaultValues={{
            countryCode: country.code,
            clientTarget: "own",
            isForSeller: true,
            ownShipping: true,
            paymentMethod: "ONLINE",
            supplierId: SEERI_SUPPLIER_ID,
            type: "SELL_OUT",
          }}
        >
          <PrePurchaseListener />
          <V2Layout />
        </SimpleForm>
      </Create>
    </>
  );
};

const V2Layout = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdmin();
  const [showAdvanced, setShowAdvanced] = useState(false);

  return (
    <Container maxWidth="xl" disableGutters sx={{ py: { xs: 1, md: 2 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <IconButton
            size="small"
            onClick={() => navigate(`/${PURCHASES}`)}
            sx={{ border: 1, borderColor: "divider" }}
          >
            <ArrowBackOutlined fontSize="small" />
          </IconButton>
          <Stack>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: "0.12em", lineHeight: 1 }}
            >
              Nuevo pedido
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                lineHeight: 1.1,
                fontSize: { xs: "1.6rem", md: "2rem" },
              }}
            >
              Crear pedido v2
            </Typography>
          </Stack>
        </Stack>
        <Chip
          label="Beta · venta a cliente final"
          variant="outlined"
          size="small"
          sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
        />
      </Stack>

      <Box
        sx={{
          display: "grid",
          gap: { xs: 2, md: 3 },
          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(0, 1fr) 380px",
          },
          alignItems: "start",
        }}
      >
        <Stack spacing={2}>
          <StepCard
            n={1}
            icon={<PersonOutline />}
            title="Cliente"
            description="Buscá o creá el cliente que compra."
          >
            <ClientStep />
          </StepCard>

          <StepCard
            n={2}
            icon={<LocalShippingOutlined />}
            title="Dirección de envío"
            description="El comprador final recibe el pedido aquí."
          >
            <AddressStep />
          </StepCard>

          <FormGroupContextProvider name="products">
            <StepCard
              n={3}
              icon={<ShoppingCartOutlined />}
              title="Productos"
              description="Buscá y elegí la bodega de cada referencia."
            >
              <ProductPickerV2 />
            </StepCard>
          </FormGroupContextProvider>

          <FormGroupContextProvider name="paymentMethod">
            <StepCard
              n={4}
              icon={<PaymentOutlined />}
              title="Pago"
              description="Definí cómo paga este pedido."
            >
              <PaymentStep />
            </StepCard>
          </FormGroupContextProvider>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => setShowAdvanced((v) => !v)}
                sx={{ cursor: "pointer" }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <TuneOutlined fontSize="small" color="action" />
                  <Typography sx={{ fontWeight: 600 }}>
                    Más opciones
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Facturación, envío manual
                  </Typography>
                </Stack>
                <ExpandMore
                  sx={{
                    transform: showAdvanced ? "rotate(180deg)" : "none",
                    transition: "transform .2s",
                  }}
                />
              </Stack>
              <Collapse in={showAdvanced} timeout="auto">
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: "0.08em" }}
                  >
                    Dirección de facturación
                  </Typography>
                  <Box sx={{ mt: 0.5, mb: 2 }}>
                    <BillingAdressDropdown />
                  </Box>
                  {hasPermission("purchase.create.shipping") && (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                        }}
                      >
                        Costo de envío
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <ShippingInputs />
                      </Box>
                    </>
                  )}
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        </Stack>

        <Box
          sx={{
            position: { lg: "sticky" },
            top: { lg: 16 },
            alignSelf: "start",
          }}
        >
          <SummaryPanel />
        </Box>
      </Box>
    </Container>
  );
};

const AddressStep: FC = () => {
  const sellerId = useWatch({ name: "sellerId" }) as string | undefined;
  const { setValue } = useFormContext();
  const [open, setOpen] = useState(false);
  const refresh = useRefresh();

  return (
    <Stack spacing={1.5}>
      <ClientOption />
      {sellerId && (
        <Box>
          <RaButton label="Crear dirección" onClick={() => setOpen(true)}>
            <CreateRounded />
          </RaButton>
        </Box>
      )}
      {sellerId && (
        <AddressModalV2
          open={open}
          onClose={() => setOpen(false)}
          sellerId={sellerId}
          onCreated={(address) => {
            refresh();
            if (address?.id) {
              setValue("addressId", address.id, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }
            // Touch SELLER_ADDRESSES cache marker (silences unused import lint)
            void SELLER_ADDRESSES;
          }}
        />
      )}
    </Stack>
  );
};

const ClientStep: FC = () => {
  const [open, setOpen] = useState(false);
  const { setValue } = useFormContext();
  return (
    <Stack spacing={1.5}>
      <ClientAutocomplete />
      <Box>
        <RaButton label="Crear cliente" onClick={() => setOpen(true)}>
          <CreateRounded />
        </RaButton>
      </Box>
      <ClientModalV2
        open={open}
        onClose={() => setOpen(false)}
        onCreated={(client) => {
          if (client?.id) {
            setValue("sellerId", client.id, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }
        }}
      />
    </Stack>
  );
};

const StepCard: FC<{
  n: number;
  icon: ReactNode;
  title: string;
  description: string;
  children: ReactNode;
}> = ({ n, icon, title, description, children }) => (
  <Card variant="outlined" sx={{ borderRadius: 2 }}>
    <CardContent>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={2}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "action.hover",
            color: "primary.main",
            position: "relative",
            flexShrink: 0,
          }}
        >
          {icon}
          <Box
            sx={{
              position: "absolute",
              top: -4,
              right: -4,
              width: 18,
              height: 18,
              borderRadius: "50%",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              fontSize: "0.65rem",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {n}
          </Box>
        </Box>
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        </Stack>
      </Stack>
      <Divider sx={{ mb: 2 }} />
      {children}
    </CardContent>
  </Card>
);

const SummaryPanel = () => {
  const prePurchase = useAtomValue(prePurchaseAtom);
  const error = useAtomValue(prePurchaseErrorAtom);
  const { isValid, isValidating } = useFormState();
  const notify = useNotify();

  const total = prePurchase?.total ?? 0;
  const itemCount =
    prePurchase?.products?.reduce(
      (acc: number, p: any) => acc + (p.quantity ?? 0),
      0
    ) ?? 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box
        sx={{
          p: 2.5,
          background:
            "linear-gradient(135deg, rgba(253,77,116,0.08), rgba(1,229,220,0.08))",
        }}
      >
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ letterSpacing: "0.12em" }}
        >
          Total estimado
        </Typography>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1.8rem", md: "2.2rem" },
            lineHeight: 1.1,
            mt: 0.5,
            color: prePurchase ? "text.primary" : "text.disabled",
            transition: "color .25s",
          }}
        >
          {prePurchase ? formatTotal(total) : "—"}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          {itemCount === 0
            ? "Aún sin productos"
            : `${itemCount} unidad${itemCount === 1 ? "" : "es"}`}
        </Typography>
      </Box>

      {isValidating && (
        <LinearProgress
          sx={{ height: 2, "& .MuiLinearProgress-bar": { transition: "none" } }}
        />
      )}

      <Box sx={{ p: 2.5 }}>
        {error ? (
          <Stack spacing={1.5} alignItems="flex-start">
            <Stack direction="row" spacing={1} alignItems="center">
              <ErrorOutline color="error" fontSize="small" />
              <Typography variant="subtitle2" color="error">
                No pudimos calcular el total
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {(error as any)?.response?.data?.message ??
                "Revisá vendedor, dirección y productos."}
            </Typography>
          </Stack>
        ) : prePurchase ? (
          <PricingV2 purchase={prePurchase} />
        ) : (
          <Stack spacing={1} alignItems="center" sx={{ py: 3 }}>
            <CheckCircleOutline
              sx={{ fontSize: 32, color: "text.disabled" }}
            />
            <Typography variant="body2" color="text.secondary" align="center">
              Llená vendedor, dirección y al menos un producto para ver el
              total.
            </Typography>
          </Stack>
        )}
      </Box>

      <Divider />
      <Box sx={{ p: 2 }}>
        <ConfirmToolbar
          disabled={!prePurchase || !isValid}
          onError={(e: any) =>
            notify(e?.message ?? "No se pudo crear el pedido", {
              type: "error",
            })
          }
        />
      </Box>
    </Paper>
  );
};

const ConfirmToolbar: FC<{ disabled: boolean; onError: (e: any) => void }> = ({
  disabled,
}) => (
  <Toolbar
    sx={{
      p: 0,
      background: "transparent",
      minHeight: "auto !important",
    }}
  >
    <SaveButton
      type="button"
      label="Confirmar pedido"
      icon={<CheckCircleOutline />}
      disabled={disabled}
      variant="contained"
      sx={{
        flex: 1,
        py: 1.25,
        fontWeight: 700,
        letterSpacing: "0.02em",
      }}
    />
  </Toolbar>
);

const formatTotal = (n: number) =>
  new Intl.NumberFormat(country.locale ?? "es-CO", {
    style: "currency",
    currency: country.currency ?? "COP",
    maximumFractionDigits: 0,
  }).format(n || 0);
